// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

type LinkTableCardModalType = {
  open: boolean;
  onClose: () => void;
  onConfirm: (examId: string) => void;
};

// Link modal that opens from the exam cards on the Patient/Dashboard pages
const LinkTableCardModal = ({
  open,
  onClose,
  onConfirm,
}: LinkTableCardModalType) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { unlinkedDeviceOptions } = useDevicesContext();

  const [selectedDevice, setSelectedDevice] = useState({
    displayName: "",
    value: "",
  });

  const handleSetDevice = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = unlinkedDeviceOptions.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSelectedDevice({ ...value });
      }
    },
    [unlinkedDeviceOptions]
  );

  const shouldDisable = useMemo(() => {
    const invalidDevices =
      unlinkedDeviceOptions.length === 0 ||
      (unlinkedDeviceOptions.length > 0 &&
        !selectedDevice.displayName &&
        !selectedDevice.value);

    return invalidDevices;
  }, [unlinkedDeviceOptions, selectedDevice]);

  const resetValues = useCallback(() => {
    setSelectedDevice({ displayName: "", value: "" });
  }, []);

  const handleClose = useCallback(() => {
    resetValues();
    onClose();
  }, [resetValues, onClose]);

  const handleConfirm = useCallback(() => {
    resetValues();
    onConfirm(selectedDevice?.value);
  }, [resetValues, onConfirm, selectedDevice?.value]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={handleClose}
      title={t("Link Device to Exam")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={handleConfirm}
          >
            {t("Link")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        {unlinkedDeviceOptions && unlinkedDeviceOptions?.length > 0 ? (
          <Box display="flex" flexDirection="column" gap="4px">
            <Typography fontSize="14px">
              {t("Device")}
              <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
            </Typography>
            <SelectDropdown
              id="link-table-card-device-dropdown"
              required
              dropdownOptions={unlinkedDeviceOptions}
              value={selectedDevice?.displayName}
              handleChange={handleSetDevice}
            />
          </Box>
        ) : (
          <Typography>{t("No unlinked devices found.")}</Typography>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default LinkTableCardModal;
